import React, { useEffect, useRef, useState } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { container } from "@/utils/mobx";
import { appStore } from "@/app_store";
import { DeploymentSectionStore, DeploymentService, BrowserSession, TerminalSession, SubTab } from "./store";
import { useInputEvent } from "@/components/toolkit/InputProvider";
import { BrowserStreamModal } from "./BrowserStreamModal";
import { TerminalModal } from "./TerminalModal";
import { ConfigureDeploymentEnvModal } from "@/components/ConfigureDeploymentEnvModal";
import { TextInput } from "@/components/toolkit/TextInput";
import { SelectInput } from "@/components/toolkit/SelectInput";
import { Accordion, AccordionItem } from "@/components/toolkit/Accordion";
import { ConfirmDialog } from "@/components/toolkit/ConfirmDialog";
import { Split, SplitPane } from "@/components/toolkit/Split";
import { t } from "@/i18n";
import { ansiToHtml } from "@/utils/ansi";
import { SWIPE_GUARD_CLASS } from "@/utils/swipe";
import "./s.deploy_section.css";

const SUB_TABS: Array<{ key: SubTab; label: string }> = [
    { key: "services", label: "Services" },
    { key: "browser", label: "Browser" },
    { key: "terminal", label: "Terminal" },
];

// Log pane is virtualized (react-virtuoso) so only on-screen lines mount —
// otherwise server-side scrollback rotation splices the array and React
// re-patches every row's innerHTML on each new line. Spacers give the list
// top/bottom breathing room the old padding used to.
const LogSpacer = () => <div style={{ height: 8 }} />;
const logVirtuosoComponents = { Header: LogSpacer, Footer: LogSpacer };
// Render this many px of extra rows above/below the viewport so scrolling
// reveals nearby lines instantly instead of flashing blank, without mounting
// the whole scrollback.
const LOG_OVERSCAN_PX = 800;
// Sentinel row appended while deploying to render the blinking cursor.
const CURSOR_ROW = Symbol("cursor");

function ServiceIcon({ name }: { name: string }) {
    const lower = name.toLowerCase();
    if (lower.includes("proxy")) {
        return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
        );
    }
    if (lower.includes("backend") || lower.includes("api") || lower.includes("server")) {
        return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                <line x1="6" y1="6" x2="6.01" y2="6"/>
                <line x1="6" y1="18" x2="6.01" y2="18"/>
            </svg>
        );
    }
    if (lower.includes("frontend") || lower.includes("web") || lower.includes("ui")) {
        return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
        );
    }
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        </svg>
    );
}

function TerminalIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 17 10 11 4 5"/>
            <line x1="12" y1="19" x2="20" y2="19"/>
        </svg>
    );
}

const ServiceCard = ({ svc, store }: { svc: DeploymentService; store: DeploymentSectionStore }) => (
    <div className="deploy_section__card">
        <div className="deploy_section__card_header">
            <div className="deploy_section__card_icon_placeholder">
                <ServiceIcon name={svc.name} />
            </div>
            <div className="deploy_section__card_meta">
                <div className="deploy_section__card_name">{svc.name}</div>
            </div>
            {svc.status && (
                <span className={`deploy_section__card_status_badge deploy_section__card_status--${svc.status}`}>
                    {svc.status}
                </span>
            )}
        </div>
        <div className="deploy_section__card_footer">
            <button
                className="deploy_section__card_btn"
                onClick={() => store.showServiceLogs(svc)}
            >
                Logs
            </button>
            <button
                className="deploy_section__card_btn deploy_section__card_btn--deploy"
                onClick={() => store.deployService(svc)}
            >
                Deploy
            </button>
            <button
                className="deploy_section__card_btn deploy_section__card_btn--terminal"
                onClick={() => store.openServiceTerminal(svc)}
                title={`Open terminal for ${svc.name}`}
            >
                <TerminalIcon />
            </button>
        </div>
    </div>
);

const NewTerminalModal = ({ store, onClose }: { store: DeploymentSectionStore; onClose: () => void }) => {
    const [selectedEnv, setSelectedEnv] = useState(store.activeEnv || store.envs[0] || "");
    const [selectedService, setSelectedService] = useState<string | null>(null);

    const services = store.envList.find(e => e.name === selectedEnv)?.services ?? [];

    const handleOpen = async () => {
        onClose();
        await store.openNewInlineTerminal(selectedService, selectedEnv || null);
    };

    return (
        <div className="deploy_section__new_terminal_modal_backdrop" onClick={onClose}>
            <div className="deploy_section__new_terminal_modal" onClick={e => e.stopPropagation()}>
                <div className="deploy_section__new_terminal_modal_title">New Terminal</div>
                {store.envs.length > 1 && (
                    <div className="deploy_section__new_terminal_modal_section">
                        <div className="deploy_section__new_terminal_modal_label">Environment</div>
                        <div className="deploy_section__env_tabs">
                            {store.envs.map(env => (
                                <button
                                    key={env}
                                    className={"deploy_section__env_tab" + (env === selectedEnv ? " active" : "")}
                                    onClick={() => setSelectedEnv(env)}
                                >
                                    {env}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="deploy_section__new_terminal_modal_section">
                    <div className="deploy_section__new_terminal_modal_label">Service</div>
                    <div className="deploy_section__new_terminal_modal_services">
                        <button
                            className={"deploy_section__new_terminal_modal_service" + (selectedService === null ? " active" : "")}
                            onClick={() => setSelectedService(null)}
                        >
                            Generic Shell
                        </button>
                        {services.map(svc => (
                            <button
                                key={svc.name}
                                className={"deploy_section__new_terminal_modal_service" + (selectedService === svc.name ? " active" : "")}
                                onClick={() => setSelectedService(svc.name)}
                            >
                                {svc.name}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="deploy_section__new_terminal_modal_actions">
                    <button className="deploy_section__new_terminal_modal_cancel" onClick={onClose}>Cancel</button>
                    <button className="deploy_section__deploy_btn" onClick={handleOpen}>Open</button>
                </div>
            </div>
        </div>
    );
};

export const DeploymentSection = container((props: {
    store: DeploymentSectionStore;
}) => {
    const store = props.store;
    const logContentRef = useRef<HTMLDivElement>(null);
    const pinchInitialFontSizeRef = useRef(store.logFontSize);
    const [splitSizes, setSplitSizes] = useState<number[]>([Infinity, 200]);
    const [mobileAccordionIndex, setMobileAccordionIndex] = useState(0);
    const [errorExpanded, setErrorExpanded] = useState(false);
    const [showNewTerminalModal, setShowNewTerminalModal] = useState(false);
    const [closeConfirmSessionId, setCloseConfirmSessionId] = useState<string | null>(null);
    const [closeConfirmLogTabId, setCloseConfirmLogTabId] = useState<string | null>(null);
    const [showDeployEnvModal, setShowDeployEnvModal] = useState(false);
    const [deployEnvModalActiveId, setDeployEnvModalActiveId] = useState<string | null>(null);
    const newTerminalBtnRef = useRef<HTMLButtonElement>(null);
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    // Whether we auto-follow new output. followOutput only fires when the item COUNT
    // grows, but the backend caps output_lines at 1000 then just rotates, so once
    // saturated the count is constant and native follow stops -- we re-pin manually.
    //
    // We must NOT drive this off atBottomStateChange(false): during a fast burst the
    // rotating firstItemIndex shifts the anchor and Virtuoso transiently reports
    // not-at-bottom even though the user never scrolled, which would wrongly detach us.
    // So: detach only on genuine user scroll-up intent (wheel/touch); reattach on
    // Virtuoso's trustworthy atBottom=true.
    const followRef = useRef(true);
    const lastTouchYRef = useRef(0);

    const project = appStore.agent.session.project;

    const activeTab = store.activeSubTab;
    const setActiveTab = (tab: SubTab) => store.setActiveSubTab(tab);

    useEffect(() => {
        if (store.logTabs.length > 0) {
            setActiveTab("services");
            if (store.isMobile) setMobileAccordionIndex(1);
        }
    }, [store.logActionCount]);

    useEffect(() => {
        if (store.logTabs.length === 0) setMobileAccordionIndex(0);
    }, [store.logTabs.length]);


    useInputEvent('pinch', (e) => {
        if (e.phase === 'start') {
            pinchInitialFontSizeRef.current = store.logFontSize;
        } else if (e.phase === 'change') {
            const next = Math.min(32, Math.max(8, Math.round(pinchInitialFontSizeRef.current * e.scale)));
            if (next !== store.logFontSize) store.setLogFontSize(next);
        }
    }, [store], { target: logContentRef });

    const displayedServices = store.filteredServices;

    const gridContent = store.activeEnvError ? (
        <div
            className={"deploy_section__error_banner" + (store.activeEnvErrorDetail ? " deploy_section__error_banner--clickable" : "")}
            onClick={() => store.activeEnvErrorDetail && setErrorExpanded(v => !v)}
        >
            <span className="deploy_section__error_banner_icon">⚠</span>
            <div className="deploy_section__error_banner_body">
                <div className="deploy_section__error_banner_summary">
                    {store.activeEnvError}
                    {store.activeEnvErrorDetail && (
                        <span className="deploy_section__error_banner_toggle">
                            {errorExpanded ? " ▲ hide detail" : " ▼ show detail"}
                        </span>
                    )}
                </div>
                {errorExpanded && store.activeEnvErrorDetail && (
                    <pre className="deploy_section__error_banner_detail">{store.activeEnvErrorDetail}</pre>
                )}
            </div>
        </div>
    ) : displayedServices.length === 0 ? (
        <div className="deploy_section__empty">
            {store.services.length === 0 ? "No services discovered yet." : "No services match your search."}
        </div>
    ) : (
        <div className="deploy_section__grid">
            {displayedServices.map(svc => (
                <ServiceCard key={svc.name} svc={svc} store={store} />
            ))}
        </div>
    );

    const serviceGrid = (
        <div className="deploy_section__services_scroll">
            {gridContent}
        </div>
    );

    const logTabBar = (
        <div className="deploy_section__log_pane_tabs">
            {store.logTabs.map(tab => (
                <div
                    key={tab.id}
                    className={"deploy_section__log_pane_tab" + (store.activeLogTabId === tab.id ? " active" : "")}
                    onClick={() => store.selectLogTab(tab.id)}
                >
                    <span className="deploy_section__log_pane_tab_label">{tab.label}</span>
                    <span
                        className="deploy_section__log_pane_tab_close"
                        onClick={e => { e.stopPropagation(); setCloseConfirmLogTabId(tab.id); }}
                    >
                        ×
                    </span>
                </div>
            ))}
        </div>
    );

    const logData = store.isDeploying ? [...store.deployLogs, CURSOR_ROW] : store.deployLogs;

    // Re-pin to the latest line whenever content changes while the user is at the
    // bottom. deployLogBaseIndex is in the deps because during rotation the count
    // stays flat and only the base index moves -- the signal native follow misses.
    useEffect(() => {
        if (followRef.current) {
            virtuosoRef.current?.scrollToIndex({ index: "LAST", align: "end", behavior: "auto" });
        }
    }, [logData.length, store.deployLogBaseIndex]);

    const logOutput = (
        <div className="deploy_section__log_pane_body">
            <div className="deploy_section__log_modifiers">
                <button
                    className={"deploy_section__log_modifier_btn" + (store.wordWrap ? " deploy_section__log_modifier_btn--active" : "")}
                    title={store.wordWrap ? "Disable word wrap" : "Enable word wrap"}
                    onClick={() => store.setWordWrap(!store.wordWrap)}
                >↵</button>
            </div>
            <div
                className="deploy_section__log_pane_viewport"
                ref={logContentRef}
                // Detach auto-follow only on real user scroll-up intent. wheel: deltaY<0.
                // touch: finger moving down (clientY increasing) drags content down = scroll up.
                onWheel={e => { if (e.deltaY < 0) followRef.current = false; }}
                onTouchStart={e => { lastTouchYRef.current = e.touches[0].clientY; }}
                onTouchMove={e => {
                    const y = e.touches[0].clientY;
                    if (y > lastTouchYRef.current) followRef.current = false;
                    lastTouchYRef.current = y;
                }}
            >
                {store.deployLogs.length === 0 && !store.isDeploying ? (
                    <div className="deploy_section__log_pane_empty">No output yet.</div>
                ) : (
                    <Virtuoso
                        ref={virtuosoRef}
                        className={"deploy_section__log_pane_content" + (store.wordWrap ? " deploy_section__log_pane_content--wrap" : "")}
                        style={{ fontSize: store.logFontSize }}
                        components={logVirtuosoComponents}
                        data={logData}
                        // deployLogs is a rotating window over a longer stream; firstItemIndex
                        // (abs index of line 0) gives each line a stable key as old lines evict,
                        // so the scroll anchor holds and auto-follow doesn't detach mid-burst.
                        firstItemIndex={store.deployLogBaseIndex}
                        // Mount scrolled to the latest line (restored scrollback starts at the
                        // bottom, like a terminal); followOutput + the re-pin effect keep it there.
                        initialTopMostItemIndex={Math.max(0, logData.length - 1)}
                        increaseViewportBy={LOG_OVERSCAN_PX}
                        // Reattach only on the trustworthy direction: genuinely reaching the bottom.
                        // We deliberately ignore atBottom=false here -- rotation shifts flip it
                        // transiently during fast bursts and would falsely detach us.
                        // Threshold > the 8px Footer spacer so resting at the true bottom still reads as "at bottom".
                        atBottomThreshold={16}
                        atBottomStateChange={(atBottom) => { if (atBottom) followRef.current = true; }}
                        followOutput={(isAtBottom) => isAtBottom ? "auto" : false}
                        itemContent={(_i, line) => line === CURSOR_ROW
                            ? <div className="deploy_section__log_line deploy_section__log_line--cursor">▋</div>
                            : <div className="deploy_section__log_line" dangerouslySetInnerHTML={{ __html: ansiToHtml(line as string) }} />}
                    />
                )}
            </div>
        </div>
    );

    const logPane = (
        <div className="deploy_section__log_pane">
            {logTabBar}
            {logOutput}
        </div>
    );

    const mobileLogPane = (
        <div className="deploy_section__log_pane deploy_section__log_pane--accordion">
            {logTabBar}
            {logOutput}
        </div>
    );

    let servicesBody: React.ReactNode;
    if (store.isMobile) {
        const accordionItems: AccordionItem[] = [
            {
                header: "Services",
                content: (
                    <>
                        <div className="deploy_section__toolbar deploy_section__toolbar--accordion">
                            <SelectInput
                                className="deploy_section__env_select"
                                value={store.activeEnv}
                                options={store.envs.map(env => ({ value: env, title: store.envErrors[env] ? `⚠ ${env}` : env }))}
                                onChange={v => v && store.setActiveEnv(v)}
                            />
                            <TextInput
                                className="deploy_section__search_input"
                                placeholder="Search services…"
                                value={store.searchQuery}
                                onChange={v => store.setSearchQuery(v)}
                            />
                        </div>
                        <div className="deploy_section__accordion_section">
                            {gridContent}
                        </div>
                    </>
                ),
            },
        ];
        if (store.logTabs.length > 0) {
            accordionItems.push({
                header: "Logs",
                content: mobileLogPane,
            });
        }
        servicesBody = (
            <div className="deploy_section__services_scroll">
                <Accordion
                                    items={accordionItems}
                                    openIndex={mobileAccordionIndex}
                                    onOpenChange={setMobileAccordionIndex}
                                />
            </div>
        );
    } else if (!store.isMobile && store.logTabs.length > 0) {
        servicesBody = (
            <Split
                split="horizontal"
                sizes={splitSizes}
                onChange={(s: number[]) => setSplitSizes(s)}
            >
                <SplitPane minSize={60}>{serviceGrid}</SplitPane>
                <SplitPane minSize={60}>{logPane}</SplitPane>
            </Split>
        );
    } else {
        servicesBody = serviceGrid;
    }

    const allBrowserTabs = store.allBrowserTabs;
    const effectiveActiveBrowserId = store.activeBrowserTabId &&
        allBrowserTabs.some(t => t.id === store.activeBrowserTabId)
        ? store.activeBrowserTabId
        : allBrowserTabs[allBrowserTabs.length - 1]?.id ?? null;

    const desktopBrowserContent = (
        <div className="deploy_section__browser_panel">
            <div className={`deploy_section__browser_tabs_bar ${SWIPE_GUARD_CLASS}`}>
                {allBrowserTabs.map(tab => (
                    <div
                        key={tab.id}
                        className={"deploy_section__browser_tab" + (tab.id === effectiveActiveBrowserId ? " active" : "")}
                        onClick={() => store.setActiveBrowserTab(tab.id)}
                    >
                        <span className="deploy_section__browser_tab_label">
                            {tab.session ? (tab.session.name || tab.session.current_url || "New Tab") : "New Tab"}
                        </span>
                        <span
                            className="deploy_section__browser_tab_close"
                            onClick={e => { e.stopPropagation(); void store.removeBrowserSession(tab.id); }}
                        >×</span>
                    </div>
                ))}
                <button
                    className="deploy_section__browser_new_tab_btn"
                    onClick={() => store.openInlineBrowserTab()}
                    title="New tab"
                >+</button>
            </div>
            <div className="deploy_section__browser_content">
                {allBrowserTabs.length === 0 ? (
                    <div className="deploy_section__browser_empty">
                        <button
                            className="deploy_section__deploy_btn"
                            onClick={() => store.openInlineBrowserTab()}
                        >New Tab</button>
                    </div>
                ) : (
                    allBrowserTabs.map(tab => (
                        <BrowserStreamModal
                            key={tab.id}
                            isOpen={tab.id === effectiveActiveBrowserId}
                            browserSessionId={tab.id}
                            session={tab.session}
                            whepUrl={tab.session?.whep_url ?? null}
                            url={tab.session?.current_url ?? ""}
                            onClose={() => void store.removeBrowserSession(tab.id)}
                        />
                    ))
                )}
            </div>
        </div>
    );

    const effectiveActiveTerminalId = store.activeTerminalTabId &&
        store.terminalSessions.some(s => s.id === store.activeTerminalTabId)
        ? store.activeTerminalTabId
        : store.terminalSessions[0]?.id ?? null;

    const desktopTerminalContent = (
        <div className="deploy_section__terminal_panel">
            <div className={`deploy_section__terminal_tabs_bar ${SWIPE_GUARD_CLASS}`}>
                {store.terminalSessions.map(session => (
                    <div
                        key={session.id}
                        className={"deploy_section__terminal_tab" + (session.id === effectiveActiveTerminalId ? " active" : "")}
                        onClick={() => store.setActiveTerminalTab(session.id)}
                    >
                        <span className="deploy_section__terminal_tab_label">{session.name || session.id}</span>
                        <span
                            className="deploy_section__terminal_tab_close"
                            onClick={e => { e.stopPropagation(); setCloseConfirmSessionId(session.id); }}
                        >×</span>
                    </div>
                ))}
                <div className="deploy_section__terminal_new_tab_wrapper">
                    <button
                        ref={newTerminalBtnRef}
                        className="deploy_section__terminal_new_tab_btn"
                        onClick={() => setShowNewTerminalModal(true)}
                        title="New terminal"
                    >+</button>
                </div>
            </div>
            <div className="deploy_section__terminal_content">
                {store.terminalSessions.length === 0 ? (
                    <div className="deploy_section__terminal_empty">
                        <button
                            className="deploy_section__deploy_btn"
                            onClick={() => setShowNewTerminalModal(true)}
                        >New Terminal</button>
                    </div>
                ) : (
                    store.terminalSessions.map(session => (
                        <TerminalModal
                            key={session.id}
                            sessionId={session.id}
                            isNew={store.newTerminalSessionIds.has(session.id)}
                            isOpen={session.id === effectiveActiveTerminalId}
                            label={session.name}
                            onClose={() => setCloseConfirmSessionId(session.id)}
                        />
                    ))
                )}
            </div>
        </div>
    );

    return (
        <>
            <div className={"deploy_section" + (store.isMobile ? " deploy_section--mobile" : "")}>
                <div className="deploy_section__header">
                    <div className="deploy_section__tabs">
                        {SUB_TABS.map(tab => (
                            <button
                                key={tab.key}
                                className={"deploy_section__tab" + (activeTab === tab.key ? " active" : "")}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <button
                        className="deploy_section__configure_btn"
                        title="Configure deployment environments"
                        onClick={() => {
                            const firstEnvId = Object.keys(project.deployment?.envs ?? {})[0] ?? null;
                            setDeployEnvModalActiveId(firstEnvId);
                            setShowDeployEnvModal(true);
                        }}
                    >
                        Configure
                    </button>
                </div>

                {activeTab === "services" && (
                    <>
                        {!store.isMobile && (
                            <div className="deploy_section__toolbar">
                                <SelectInput
                                    className="deploy_section__env_select"
                                    value={store.activeEnv}
                                    options={store.envs.map(env => ({ value: env, title: store.envErrors[env] ? `⚠ ${env}` : env }))}
                                    onChange={v => v && store.setActiveEnv(v)}
                                />
                                <TextInput
                                    className="deploy_section__search_input"
                                    placeholder="Search services…"
                                    value={store.searchQuery}
                                    onChange={v => store.setSearchQuery(v)}
                                />
                            </div>
                        )}
                        <div className="deploy_section__body">
                            {servicesBody}
                        </div>
                    </>
                )}

                {activeTab === "browser" && (
                    <div className="deploy_section__body">
                        {desktopBrowserContent}
                    </div>
                )}

                {activeTab === "terminal" && (
                    <div className="deploy_section__body">
                        {desktopTerminalContent}
                    </div>
                )}
            </div>
            {showNewTerminalModal && (
                <NewTerminalModal
                    store={store}
                    onClose={() => setShowNewTerminalModal(false)}
                />
            )}
            {closeConfirmSessionId !== null && (
                <div className="deploy_section__new_terminal_modal_backdrop" onClick={() => setCloseConfirmSessionId(null)}>
                    <div className="deploy_section__new_terminal_modal" onClick={e => e.stopPropagation()} style={{ minWidth: 260, maxWidth: 340 }}>
                        <div className="deploy_section__new_terminal_modal_title">Close terminal?</div>
                        <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
                            The session and its history will be lost.
                        </div>
                        <div className="deploy_section__new_terminal_modal_actions">
                            <button className="deploy_section__new_terminal_modal_cancel" onClick={() => setCloseConfirmSessionId(null)}>Cancel</button>
                            <button
                                className="deploy_section__deploy_btn"
                                onClick={() => { store.closeTerminalSession(closeConfirmSessionId); setCloseConfirmSessionId(null); }}
                            >Close</button>
                        </div>
                    </div>
                </div>
            )}
            {closeConfirmLogTabId !== null && (() => {
                const tab = store.logTabs.find(t => t.id === closeConfirmLogTabId);
                const process = tab
                    ? store.deploymentContext?.processes.processes.find(p => p.name === tab.label)
                    : null;
                const running = process ? !process.is_finished : false;
                return (
                    <ConfirmDialog
                        title="Close tab?"
                        message={running
                            ? "This process is still running. Closing the tab will stop it."
                            : "Closing the tab will discard its output."}
                        confirmLabel="Close"
                        danger
                        onConfirm={() => { store.closeLogTab(closeConfirmLogTabId); setCloseConfirmLogTabId(null); }}
                        onCancel={() => setCloseConfirmLogTabId(null)}
                    />
                );
            })()}
            {showDeployEnvModal && project && (
                <ConfigureDeploymentEnvModal
                    project={project}
                    activeId={deployEnvModalActiveId}
                    onActiveIdChange={setDeployEnvModalActiveId}
                    onClose={() => setShowDeployEnvModal(false)}
                />
            )}
        </>
    );
}, DeploymentSectionStore);
